import { Connection, Edge, Node } from "@xyflow/react";
import { SequenceNodeData } from "./SequenceNode";
import { InteractionType } from "../../models/interactions/Interaction";

const SINGLE_CONNECTION_EXCLUDED_TYPES = new Set<InteractionType>([
  InteractionType.Sequence,
  InteractionType.SubSequence,
  InteractionType.Trigger,
]);

const PARALLEL_SECONDARY_OUTPUT_HANDLE_ID = "secondary-output";

const normalizeHandleId = (handleId?: string | null): string | null =>
  handleId ?? null;

export function buildNodeTypeByIdMap(
  nodes?: Node<SequenceNodeData>[]
): Map<string, InteractionType> {
  const nodeTypeById = new Map<string, InteractionType>();
  nodes?.forEach((node) => {
    const interactionType = node.data?.interaction?.type;
    if (interactionType) {
      nodeTypeById.set(node.id, interactionType);
    }
  });
  return nodeTypeById;
}

export function applySingleConnectionRules(
  currentEdges: Edge[],
  connection: Connection,
  nodeTypeById: Map<string, InteractionType>
): Edge[] {
  if (!connection.source || !connection.target) {
    return currentEdges;
  }

  const sourceType = nodeTypeById.get(connection.source);
  const targetType = nodeTypeById.get(connection.target);
  const sourceHandle = normalizeHandleId(connection.sourceHandle);
  const targetHandle = normalizeHandleId(connection.targetHandle);
  const isParallelSecondaryOutput =
    sourceType === InteractionType.ParallelSequence &&
    sourceHandle === PARALLEL_SECONDARY_OUTPUT_HANDLE_ID;

  return currentEdges.filter((edge) => {
    const matchesSameSourceHandle =
      edge.source === connection.source &&
      normalizeHandleId(edge.sourceHandle) === sourceHandle;
    const matchesSameTargetHandle =
      edge.target === connection.target &&
      normalizeHandleId(edge.targetHandle) === targetHandle;

    const shouldRemoveBySource =
      matchesSameSourceHandle &&
      !isParallelSecondaryOutput &&
      sourceType !== undefined &&
      !SINGLE_CONNECTION_EXCLUDED_TYPES.has(sourceType);
    const shouldRemoveByTarget =
      matchesSameTargetHandle &&
      targetType !== undefined &&
      !SINGLE_CONNECTION_EXCLUDED_TYPES.has(targetType);

    return !shouldRemoveBySource && !shouldRemoveByTarget;
  });
}
